import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  isDevMode,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { IonInput, ModalController } from '@ionic/angular';
import { GoogleMapsService } from '../../services/google-maps.service';
import { ControllerService } from '../../services/controller.service';
import { Coordinate, Location } from '../../interfaces/location';
import { GeocodingStatus } from '../../enums/geocoding-status';

@Component({
  selector: 'app-modal-location-picker',
  templateUrl: './modal-location-picker.component.html',
  styleUrls: ['./modal-location-picker.component.scss'],
})
export class ModalLocationPickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() location: Location;

  @ViewChild('mapContainer') mapContainer: ElementRef;
  @ViewChild('inputAutocomplete', { static: true }) inputAutocomplete: IonInput;

  address: string;

  private googleMaps: any;
  private clickListener: any;
  private map: any;
  private marker: any;
  private autocomplete: any;
  private autocompletePlaceChangedListener: any;

  constructor(
    private renderer: Renderer2,
    private modalController: ModalController,
    private googleMapsService: GoogleMapsService,
    private controllerService: ControllerService,
  ) {}

  ngOnInit() {}

  async ngAfterViewInit() {
    try {
      this.googleMaps = await this.googleMapsService.getGoogleMaps();

      this.map = new this.googleMaps.Map(this.mapContainer.nativeElement, {
        center: {
          lat: this.location.latitude,
          lng: this.location.longitude,
        },
        minZoom: 6, // min zoom adjusted to the latLngBounds
        restriction: {
          // Indonesia latLngBounds taken from here
          // https://gist.github.com/graydon/11198540?permalink_comment_id=2824611#gistcomment-2824611
          latLngBounds: {
            east: 141.0194444, // ne lng
            north: 6.2744496, // ne lat
            south: -11.2085669, // sw lat
            west: 94.7717124, // sw lng
          },
          strictBounds: true,
        },
        zoom: 18,
      });

      this.marker = new this.googleMaps.Marker({
        map: this.map,
        position: {
          lat: this.location.latitude,
          lng: this.location.longitude,
        },
      });

      const nativeInputElement = await this.inputAutocomplete.getInputElement();

      this.autocomplete = new this.googleMaps.places.Autocomplete(nativeInputElement);

      this.autocomplete.bindTo('bounds', this.map);

      this.googleMaps.event.addListenerOnce(this.map, 'idle', () => {
        this.renderer.addClass(this.mapContainer.nativeElement, 'visible');
      });

      this.clickListener = this.map.addListener('click', async mapsMouseEvent => {
        this.location.latitude = mapsMouseEvent.latLng.lat();

        this.location.longitude = mapsMouseEvent.latLng.lng();

        this.updateAddress(this.location.latitude, this.location.longitude);

        this.map.panTo({
          lat: this.location.latitude,
          lng: this.location.longitude,
        });

        this.marker.setPosition({
          lat: this.location.latitude,
          lng: this.location.longitude,
        });

        this.location.googleMapsSearchUrl = this.googleMapsService.getGoogleMapsSearchUrl(
          this.location.latitude,
          this.location.longitude,
          mapsMouseEvent.placeId || null,
        );
      });

      this.autocompletePlaceChangedListener = this.autocomplete.addListener('place_changed', async () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
          await this.controllerService.presentAlert({
            header: 'Info',
            message: 'Lokasi tidak ditemukan',
          });

          return;
        }

        const address_components = place?.address_components;
        const provinsi = address_components.find(item => item.types.includes('administrative_area_level_1'))?.long_name;
        const kab_kota = address_components.find(item => item.types.includes('administrative_area_level_2'))?.long_name;
        const kecamatan = address_components.find(item =>
          item.types.includes('administrative_area_level_3'),
        )?.long_name;
        const postal_code = address_components.find(item => item.types.includes('postal_code'))?.long_name;

        this.location.provinsi = provinsi;
        this.location.kab_kota = kab_kota;
        this.location.kecamatan = kecamatan;
        this.location.postal_code = postal_code;

        this.location.latitude = place.geometry.location.lat();
        this.location.longitude = place.geometry.location.lng();

        this.location.googleMapsAddress = this.takeDetailAddress(place.formatted_address);

        this.map.panTo({
          lat: this.location.latitude,
          lng: this.location.longitude,
        });

        this.marker.setTitle(place.name);

        this.marker.setPosition({
          lat: this.location.latitude,
          lng: this.location.longitude,
        });

        this.location.googleMapsSearchUrl =
          place.url ||
          this.googleMapsService.getGoogleMapsSearchUrl(
            this.location.latitude,
            this.location.longitude,
            place.place_id,
          );

        // this.updateAddress(this.location.latitude, this.location.longitude);
      });

      // this.clickListener = this.map.addListener('click', async mapsMouseEvent => {
      //   const coordinate = mapsMouseEvent.latLng.toJSON();

      //   this.location.latitude = coordinate.lat;

      //   this.location.longitude = coordinate.lng;

      //   this.map.setCenter(coordinate);

      //   this.marker.setPosition(coordinate);

      //   this.updateAddress(coordinate.lat, coordinate.lng);
      // });

      // this.updateAddress(this.location.latitude, this.location.longitude);
    } catch (error) {
      if (isDevMode()) {
        console.log(error);
      }

      await this.controllerService.presentAlert(
        {
          header: 'Error Google Maps Javascript API',
          message: 'Terjadi kesalahan ketika memuat google maps, hubungi customer service!',
          buttons: [
            {
              text: 'OK',
              handler: async () => {
                await this.modalController.dismiss();
              },
            },
          ],
        },
        true,
      );
    }
  }

  ngOnDestroy() {
    if (this.clickListener) {
      this.googleMaps.event.removeListener(this.clickListener);
    }

    if (this.autocompletePlaceChangedListener) {
      this.googleMaps.event.removeListener(this.autocompletePlaceChangedListener);
    }
  }

  onSearch(event: Event) {
    const term = (event.target as HTMLInputElement).value;

    if (!term) {
      return;
    }

    this.googleMapsService.geocoding(term).subscribe(async response => {
      if (![GeocodingStatus.Ok, GeocodingStatus.ZeroResults].includes(response.status)) {
        await this.controllerService.presentAlert(
          {
            header: 'Error Google Maps Geocoding API',
            message: 'Terjadi kesalahan ketika mencari alamat pada google maps',
          },
          true,
        );

        return;
      }

      if (!response.results.length) {
        await this.controllerService.presentAlert(
          {
            header: 'Google Maps Geocoding API Result',
            message: 'Alamat tidak ditemukan pada google maps',
          },
          true,
        );

        return;
      }

      const { formatted_address, geometry, address_components } = response.results[0];

      const provinsi = address_components.find(item => item.types.includes('administrative_area_level_1'))?.long_name;
      const kab_kota = address_components.find(item => item.types.includes('administrative_area_level_2'))?.long_name;
      const kecamatan = address_components.find(item => item.types.includes('administrative_area_level_3'))?.long_name;
      const postal_code = address_components.find(item => item.types.includes('postal_code'))?.long_name;

      this.location = {
        latitude: geometry.location.lat,
        longitude: geometry.location.lng,
      };

      this.location.provinsi = provinsi;
      this.location.kab_kota = kab_kota;
      this.location.kecamatan = kecamatan;
      this.location.postal_code = postal_code;

      this.address = formatted_address;

      this.map.setCenter(geometry.location);

      this.marker.setPosition(geometry.location);
    });
  }

  async onSubmit() {
    await this.modalController.dismiss(this.location);
    // await this.controllerService.presentLoading({
    //   message: 'Loading',
    // });

    // this.googleMapsService
    //   .reverseGeocoding(this.location.latitude, this.location.longitude)
    //   .subscribe({
    //     next: async ({ results }) => {
    //       // console.log(results)
    //       if (!results.length) {
    //         await this.controllerService.presentAlert({
    //           header: 'Info',
    //           message: 'Alamat tidak ditemukan pada google maps'
    //         });

    //         return;
    //       }

    //       this.location.googleMapsAddress = results[0].formatted_address;
    //     },
    //     error: async () => {
    //       await this.controllerService.presentAlert({
    //         header: 'Error',
    //         message: 'Terjadi kesalahan ketika mencari alamat pada google maps'
    //       });
    //     },
    //     complete: async () => {
    //       await this.modalController.dismiss(this.location);

    //       await this.controllerService.dismissLoading();
    //     }
    //   });
  }

  async onClose() {
    await this.modalController.dismiss();
  }

  takeDetailAddress(fullAddress: string) {
    return fullAddress.toLowerCase().split(', kec')[0];
  }

  private updateAddress(latitude: number, longitude: number) {
    this.googleMapsService.reverseGeocoding(latitude, longitude).subscribe(response => {
      if (!response.results.length || ![GeocodingStatus.Ok, GeocodingStatus.ZeroResults].includes(response.status)) {
        return;
      }

      const { address_components } = response.results[0];

      console.log(address_components);

      const provinsi = address_components.find(item => item.types.includes('administrative_area_level_1'))?.long_name;
      const kab_kota = address_components.find(item => item.types.includes('administrative_area_level_2'))?.long_name;
      const kecamatan = address_components.find(item => item.types.includes('administrative_area_level_3'))?.long_name;
      const postal_code = address_components.find(item => item.types.includes('postal_code'))?.long_name;

      this.location.provinsi = provinsi;
      this.location.kab_kota = kab_kota;
      this.location.kecamatan = kecamatan;
      this.location.postal_code = postal_code;

      this.address = this.takeDetailAddress(response.results[0].formatted_address);
      this.location.googleMapsAddress = this.address;
    });
  }
}
